import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, CalendarDays, Users, HelpCircle, Home, Pill, MessageSquare, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { AddMedicationDialog } from '@/components/common/AddMedicationDialog';

export interface ScheduleMenuButtonProps {
  className?: string;
  scrollToMedicationSchedule?: () => void;
  handleAddMedicationExternal?: () => void;
  hideAddMedicationButton?: boolean;
  /** 
   * Callback for when the user requests a manual refresh of the medication schedule
   * @returns Promise that resolves when the refresh is complete
   */
  onRefreshRequested?: () => Promise<void>;
}

/**
 * ScheduleMenuButton component
 * 
 * A modern, animated menu button for quick access to navigation options within the schedule view
 * Similar to the mobile menu but accessible directly within the medication schedule
 * Now features a standalone Add Medication button for improved accessibility
 */
const ScheduleMenuButton: React.FC<ScheduleMenuButtonProps> = ({ 
  className, 
  scrollToMedicationSchedule,
  handleAddMedicationExternal,
  hideAddMedicationButton = false
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleAddMedication = () => {
    setIsOpen(false);
    
    // If an external handler is provided, use it
    // This allows the ScheduleMenuButtonWithDialogs to control the dialog
    if (handleAddMedicationExternal) {
      handleAddMedicationExternal();
    } else {
      // Fallback behavior - trigger dialog in MedicationSchedule via event
      const event = new CustomEvent('meduler:openAddMedicationDialog');
      document.dispatchEvent(event);
      
      // In case the event listener isn't set up, provide visual feedback
      toast.info('Opening medication dialog...');
    }
  };
  
  const handleHomeClick = () => {
    setIsOpen(false);
    // If we have a scroll function, use it to stay on the page and scroll to schedule
    if (scrollToMedicationSchedule) {
      scrollToMedicationSchedule();
    } else {
      // Otherwise just make sure we're on the home page
      const currentPath = window.location.pathname;
      if (currentPath !== '/') {
        navigate('/');
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Standalone Add Medication Button */}
      {!hideAddMedicationButton && (
        <TooltipProvider>
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <Button 
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-sm h-10 w-10 p-0 flex items-center justify-center relative overflow-hidden group transition-all duration-300 hover:shadow-md hover:scale-105 focus:scale-105 active:scale-95"
                size="icon"
                onClick={handleAddMedication}
                aria-label="Add Medication"
              >
                {/* Background animation on hover */}
                <span className="absolute inset-0 bg-white/10 transform scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full ease-out -z-1"></span>
                
                {/* Icon with subtle animation */}
                <span className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                  <Plus className="h-5 w-5" />
                </span>
                <span className="sr-only">Add Medication</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-sm">Add Medication</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      {/* Menu Button */}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            className={`${className || 'bg-white dark:bg-gray-800 text-primary rounded-full shadow-sm border border-gray-200 dark:border-gray-700'} h-10 w-10 p-0 flex items-center justify-center relative overflow-hidden group transition-all duration-300 hover:border-primary/30 hover:shadow-md hover:scale-105 focus:scale-105 active:scale-95`}
            variant="ghost"
            size="icon"
          >
            {/* Background animation on hover */}
            <span className="absolute inset-0 bg-primary/5 dark:bg-primary/10 transform scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full ease-out -z-1"></span>
            
            {/* Icon with subtle animation */}
            <span className="relative z-10 transform group-hover:scale-110 transition-transform duration-300 group-hover:text-primary/90">
              {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </span>
            <span className="sr-only">Menu</span>
          </Button>
        </DropdownMenuTrigger>
      
        <DropdownMenuContent align="end" className="w-64 p-1 animate-in slide-in-from-top-5 duration-200 shadow-lg rounded-xl border-gray-200">
          <DropdownMenuLabel className="text-xs font-medium text-gray-500 px-3 py-2">Quick Actions</DropdownMenuLabel>
          
          {/* Moved the Add Medication option to a standalone button */}
          
          <DropdownMenuItem
            className="flex items-center gap-3 cursor-pointer py-3 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:bg-gray-50 dark:focus:bg-gray-800/50 transition-colors group"
            onClick={handleHomeClick}
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 group-hover:bg-gray-200 dark:group-hover:bg-gray-700/80 transition-colors">
              <Home size={16} />
            </span>
            <span className="font-medium">Home</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator className="my-1" />
          <DropdownMenuLabel className="text-xs font-medium text-gray-500 px-3 py-2">Navigation</DropdownMenuLabel>
          
          <DropdownMenuItem asChild>
            <Link 
              to="/how-it-works" 
              className="flex items-center gap-3 cursor-pointer py-3 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:bg-gray-50 dark:focus:bg-gray-800/50 transition-colors group"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30 transition-colors">
                <HelpCircle size={16} />
              </span>
              <span className="font-medium">How It Works</span>
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuItem asChild>
            <Link 
              to="/caregivers" 
              className="flex items-center gap-3 cursor-pointer py-3 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:bg-gray-50 dark:focus:bg-gray-800/50 transition-colors group"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors">
                <Users size={16} />
              </span>
              <span className="font-medium">Caregivers</span>
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuItem asChild>
            <Link 
              to="/features" 
              className="flex items-center gap-3 cursor-pointer py-3 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:bg-gray-50 dark:focus:bg-gray-800/50 transition-colors group"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 group-hover:bg-green-100 dark:group-hover:bg-green-900/30 transition-colors">
                <CalendarDays size={16} />
              </span>
              <span className="font-medium">Features</span>
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuItem asChild>
            <Link 
              to="/faq" 
              className="flex items-center gap-3 cursor-pointer py-3 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:bg-gray-50 dark:focus:bg-gray-800/50 transition-colors group"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 group-hover:bg-pink-100 dark:group-hover:bg-pink-900/30 transition-colors">
                <MessageSquare size={16} />
              </span>
              <span className="font-medium">FAQ</span>
            </Link>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator className="my-1" />
          
          <DropdownMenuItem asChild>
            <Link 
              to="/pricing" 
              className="flex items-center gap-3 cursor-pointer py-3 px-3 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 focus:bg-amber-50 dark:focus:bg-amber-900/20 transition-colors group"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 group-hover:bg-amber-100 dark:group-hover:bg-amber-900/30 transition-colors">
                <Pill size={16} />
              </span>
              <span className="font-medium text-amber-600 dark:text-amber-500">Upgrade to Plus</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ScheduleMenuButton;

export const ScheduleMenuButtonWithDialogs: React.FC<ScheduleMenuButtonProps> = (props) => {
  const [isAddMedicationOpen, setIsAddMedicationOpen] = useState(false);
  
  // Create a modified version of the base component that can control the dialog
  const handleAddMedication = () => {
    setIsAddMedicationOpen(true);
  };
  
  return (
    <>
      <ScheduleMenuButton 
        {...props} 
        // Override the base props with our specific implementation
        handleAddMedicationExternal={handleAddMedication}
      />
      
      {/* Add Medication Dialog */}
      <AddMedicationDialog 
        open={isAddMedicationOpen}
        onOpenChange={setIsAddMedicationOpen}
      />
    </>
  );
};
